import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePOLineItems, useVerifyPOLineItem, useBulkVerifyPOLineItems, POLineItem, validateLineItem } from "@/hooks/usePOLineItems";
import { POLineItemEditor } from "./POLineItemEditor";
import { AlertTriangle, CheckCircle, Edit2, Loader2, Download } from "lucide-react";
import { exportToCSV } from "@/utils/csvExport";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { POExtractionJob } from "@/hooks/usePOExtraction";

/**
 * Whether a funding cell shows a figure rather than N/A.
 *
 * A breakdown means the line was funded by a run that recorded one, and the
 * figure is shown even when it is zero -- a matched line worth nothing is not
 * the same as an unmatched line, which is why this stopped keying off the
 * amount alone.
 *
 * The stored amount still counts on its own, though: lines funded before
 * breakdowns were recorded have a positive value and no nested object, and
 * keying only off the breakdown flipped those to N/A until someone happened to
 * recalculate the PO. The tooltip has always had a wording for that case.
 */
function showsFunding(item: POLineItem, amount: number | null | undefined): boolean {
  return item.extraction_source?.sku_report_breakdown != null || (amount ?? 0) > 0;
}

interface POLineItemsTableProps {
  purchaseOrderId: string;
  userEmail: string;
  poTotal?: number | null;
  extractionJob?: POExtractionJob | null;
  poFileName?: string;
}

const TOLERANCE = 0.01;

export function POLineItemsTable({ purchaseOrderId, userEmail, poTotal, extractionJob, poFileName }: POLineItemsTableProps) {
  const { data: lineItems, isLoading, error } = usePOLineItems(purchaseOrderId);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [editingItem, setEditingItem] = useState<POLineItem | null>(null);
  const verifyMutation = useVerifyPOLineItem();
  const bulkVerifyMutation = useBulkVerifyPOLineItems();
  const { toast } = useToast();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8 text-destructive">
        Error loading line items: {error.message}
      </div>
    );
  }

  if (!lineItems || lineItems.length === 0) {
    // Check if extraction is still in progress
    const isExtracting = extractionJob &&
      !['completed', 'failed'].includes(extractionJob.status);

    if (isExtracting) {
      return (
        <div className="flex flex-col items-center justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
          <p className="text-sm font-medium">Extracting line items...</p>
          <p className="text-sm text-muted-foreground mt-1">
            This may take a moment while we process your document.
          </p>
        </div>
      );
    }

    // Check if extraction completed but no text was extracted (scanned/image PDF)
    const isEmptyExtraction = extractionJob &&
      extractionJob.status === 'completed' &&
      extractionJob.extraction_result &&
      !extractionJob.extraction_result.text;

    if (isEmptyExtraction) {
      return (
        <div className="text-center p-8 text-muted-foreground">
          <p className="font-medium">No text could be extracted from this PDF</p>
          <p className="text-sm mt-1">Document may be scanned or image-based. Try uploading a text-based PDF.</p>
        </div>
      );
    }

    return (
      <div className="text-center p-8 text-muted-foreground">
        No line items found. Run extraction on a PO file to populate this table.
      </div>
    );
  }

  const formatCurrency = (value: number, maxDecimals = 2) => {
    // Show up to maxDecimals, but at least 2
    const decimals = Math.max(2, Math.min(maxDecimals, countSignificantDecimals(value)));
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: decimals,
    }).format(value);
  };

  const countSignificantDecimals = (value: number): number => {
    const str = value.toString();
    const dotIndex = str.indexOf('.');
    if (dotIndex === -1) return 0;
    // Trim trailing zeros
    return str.replace(/0+$/, '').length - dotIndex - 1;
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedItems(new Set(lineItems.map((item) => item.id)));
    } else {
      setSelectedItems(new Set());
    }
  };

  const handleSelectItem = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedItems);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedItems(newSelected);
  };

  const handleVerifyItem = async (id: string) => {
    try {
      await verifyMutation.mutateAsync({ id, email: userEmail });
      toast({ title: "Item verified" });
    } catch (err) {
      toast({ title: "Failed to verify item", variant: "destructive" });
    }
  };

  const handleBulkVerify = async () => {
    if (selectedItems.size === 0) return;

    try {
      await bulkVerifyMutation.mutateAsync({
        ids: Array.from(selectedItems),
        email: userEmail,
        purchaseOrderId,
      });
      setSelectedItems(new Set());
      toast({ title: `${selectedItems.size} items verified` });
    } catch (err) {
      toast({ title: "Failed to verify items", variant: "destructive" });
    }
  };

  const itemsNeedingReview = lineItems.filter((item) => item.needs_review);
  const itemsWithMathErrors = lineItems.filter((item) => !validateLineItem(item).isValid);

  // PO total validation
  const sumOfLineTotals = lineItems.reduce((sum, item) => sum + item.line_total, 0);
  const poTotalDifference = poTotal !== null && poTotal !== undefined
    ? Math.abs(sumOfLineTotals - poTotal)
    : 0;
  const isPOTotalValid = poTotal === null || poTotal === undefined || poTotalDifference <= TOLERANCE;

  const handleExportCSV = () => {
    const exportColumns = [
      'Product', 'Description', 'Qty', 'Unit Price',
      'Discount %', 'Discount $', 'Line Total', 'Current Inventory',
      '80% Unit Price', '12mo Unit Sales', 'GEV', 'NEV', 'NERV',
      'Status',
    ];
    const exportData = lineItems.map((item) => {
      const b = (item.extraction_source as any)?.sku_report_breakdown;
      return {
        'Product': item.sku || '',
        'Description': item.description || '',
        'Qty': item.quantity,
        'Unit Price': item.unit_price,
        'Discount %': item.discount_percent,
        'Discount $': item.discount_amount,
        'Line Total': item.line_total,
        'Current Inventory': b?.stock_held ?? 'N/A',
        '80% Unit Price': +(item.unit_price * 0.8).toFixed(2),
        '12mo Unit Sales': b?.units_sold ?? 'N/A',
        'GEV': item.expected_gmv,
        'NEV': item.expected_nmv,
        'NERV': item.net_expected_realizable_value,
        'Status': item.verified_at ? 'Verified' : item.needs_review ? 'Review' : 'Pending',
      };
    });
    const fileName = poFileName
      ? poFileName.replace(/\.[^/.]+$/, '.csv')
      : `po-line-items-${purchaseOrderId}.csv`;
    exportToCSV(exportData, exportColumns, fileName);
  };

  return (
    <div className="space-y-4">
      {/* Bulk actions & export */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {selectedItems.size > 0 && (
            <div className="flex items-center gap-4 p-3 bg-muted rounded-lg">
              <span className="text-sm">{selectedItems.size} item(s) selected</span>
              <Button
                size="sm"
                onClick={handleBulkVerify}
                disabled={bulkVerifyMutation.isPending}
              >
                {bulkVerifyMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Verify Selected
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSelectedItems(new Set())}
              >
                Clear Selection
              </Button>
            </div>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={handleExportCSV}>
          <Download className="h-4 w-4 mr-2" />
          Download CSV
        </Button>
      </div>

      {/* PO Total mismatch alert */}
      {!isPOTotalValid && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <span className="text-sm text-red-700 dark:text-red-400">
            PO Total mismatch: Sum of line totals ({formatCurrency(sumOfLineTotals)}) does not match PO Total ({formatCurrency(poTotal!)}). Difference: {formatCurrency(poTotalDifference)}
          </span>
        </div>
      )}

      {/* Line item math errors alert */}
      {itemsWithMathErrors.length > 0 && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <span className="text-sm text-red-700 dark:text-red-400">
            {itemsWithMathErrors.length} line item(s) have math errors (Qty × Unit Price − Discount ≠ Line Total)
          </span>
        </div>
      )}

      {/* Items needing review alert */}
      {itemsNeedingReview.length > 0 && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <span className="text-sm text-amber-700 dark:text-amber-400">
            {itemsNeedingReview.length} item(s) need review
          </span>
        </div>
      )}

      {/* Table */}
      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]">
                <Checkbox
                  checked={selectedItems.size === lineItems.length}
                  onCheckedChange={handleSelectAll}
                />
              </TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead className="text-right">Unit Price</TableHead>
              <TableHead className="text-right">Discount</TableHead>
              <TableHead className="text-right">Line Total</TableHead>
              <TableHead className="text-right">Current Inventory</TableHead>
              <TableHead className="text-right">80% Unit Price</TableHead>
              <TableHead className="text-right">12mo Unit Sales</TableHead>
              <TableHead className="text-right">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className="cursor-help underline decoration-dotted">GEV</TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="font-semibold">Gross Expected Value</p>
                      <p className="text-xs text-muted-foreground">Stock Held × Avg GMV (trailing 12 months)</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableHead>
              <TableHead className="text-right">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className="cursor-help underline decoration-dotted">NEV</TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="font-semibold">Net Expected Value</p>
                      <p className="text-xs text-muted-foreground">Stock Held × Avg NMV (trailing 12 months)</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableHead>
              <TableHead className="text-right">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className="cursor-help underline decoration-dotted">NERV</TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="font-semibold">Net Expected Realizable Value</p>
                      <p className="text-xs text-muted-foreground">MIN(Stock, Units Sold) × Avg NMV × (1 − DR%)</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lineItems.map((item) => {
              const validation = validateLineItem(item);
              return (
                <TableRow
                  key={item.id}
                  className={cn(
                    item.needs_review && "bg-amber-50 dark:bg-amber-900/10",
                    !validation.isValid && "bg-red-50 dark:bg-red-900/10"
                  )}
                >
                  <TableCell>
                    <Checkbox
                      checked={selectedItems.has(item.id)}
                      onCheckedChange={(checked) =>
                        handleSelectItem(item.id, checked as boolean)
                      }
                    />
                  </TableCell>
                  <TableCell className="font-mono text-sm">{item.sku || "-"}</TableCell>
                  <TableCell className="max-w-[200px] truncate" title={item.description || ""}>
                    {item.description || "-"}
                  </TableCell>
                  <TableCell className="text-right">{item.quantity}</TableCell>
                  <TableCell className="text-right">{formatCurrency(item.unit_price, 6)}</TableCell>
                  <TableCell className="text-right">
                    {item.discount_percent > 0 && (
                      <span className="text-muted-foreground text-xs">
                        {item.discount_percent}%{" "}
                      </span>
                    )}
                    {formatCurrency(item.discount_amount)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {!validation.isValid && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <AlertTriangle className="h-4 w-4 text-red-500" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Math error: Expected {formatCurrency(validation.expectedTotal)}</p>
                              <p className="text-xs text-muted-foreground">Qty × Price − Discount = {formatCurrency(validation.expectedTotal)}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {formatCurrency(item.line_total)}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    {(() => {
                      const b = (item.extraction_source as any)?.sku_report_breakdown;
                      return b?.stock_held != null ? b.stock_held.toLocaleString() : <span className="text-muted-foreground">N/A</span>;
                    })()}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(item.unit_price * 0.8, 6)}
                  </TableCell>
                  <TableCell className="text-right">
                    {(() => {
                      const b = (item.extraction_source as any)?.sku_report_breakdown;
                      return b?.units_sold != null ? b.units_sold.toLocaleString() : <span className="text-muted-foreground">N/A</span>;
                    })()}
                  </TableCell>
                  <TableCell className="text-right">
                    {showsFunding(item, item.expected_gmv) ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger className="cursor-help">
                            {formatCurrency(item.expected_gmv ?? 0)}
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            {(() => {
                              const b = item.extraction_source?.sku_report_breakdown;
                              if (!b) return <p>GEV from SKU report</p>;
                              return (
                                <div className="space-y-1 text-xs">
                                  <p>Total GMV: {formatCurrency(b.total_gmv)} | Units Sold: {b.units_sold}</p>
                                  <p>Avg GMV: {formatCurrency(b.avg_gmv)} | Stock: {b.stock_held}</p>
                                  <p className="font-semibold">GEV = {b.stock_held} × {formatCurrency(b.avg_gmv)}</p>
                                </div>
                              );
                            })()}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : <span className="text-muted-foreground">N/A</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    {showsFunding(item, item.expected_nmv) ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger className="cursor-help">
                            {formatCurrency(item.expected_nmv ?? 0)}
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            {(() => {
                              const b = item.extraction_source?.sku_report_breakdown;
                              if (!b) return <p>NEV from SKU report</p>;
                              return (
                                <div className="space-y-1 text-xs">
                                  <p>Total NMV: {formatCurrency(b.total_nmv)} | Avg NMV: {formatCurrency(b.avg_nmv)}</p>
                                  <p>Stock: {b.stock_held}</p>
                                  <p className="font-semibold">NEV = {b.stock_held} × {formatCurrency(b.avg_nmv)}</p>
                                </div>
                              );
                            })()}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : <span className="text-muted-foreground">N/A</span>}
                  </TableCell>
                  <TableCell className="text-right font-semibold">
                    {showsFunding(item, item.net_expected_realizable_value) ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger className="cursor-help">
                            {formatCurrency(item.net_expected_realizable_value ?? 0)}
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            {(() => {
                              const b = item.extraction_source?.sku_report_breakdown;
                              if (!b) return <p>NERV from SKU report</p>;
                              return (
                                <div className="space-y-1 text-xs">
                                  <p>Stock: {b.stock_held} | Sold: {b.units_sold} | Avg NMV: {formatCurrency(b.avg_nmv)} | DR: {b.dr_percent}%</p>
                                  <p className="font-semibold">MIN({b.stock_held}, {b.units_sold}) × {formatCurrency(b.avg_nmv)} × (1 − {b.dr_percent}%) = {formatCurrency(item.net_expected_realizable_value ?? 0)}</p>
                                </div>
                              );
                            })()}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : <span className="text-muted-foreground">N/A</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          {item.verified_at ? (
                            <Badge variant="default" className="bg-primary">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Verified
                            </Badge>
                          ) : item.needs_review ? (
                            <Badge variant="outline" className="border-amber-500 text-amber-600">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Review
                            </Badge>
                          ) : (
                            <Badge variant="secondary">Pending</Badge>
                          )}
                        </TooltipTrigger>
                        <TooltipContent>
                          {item.verified_at ? (
                            <p>Verified by {item.verified_by_email}</p>
                          ) : item.needs_review ? (
                            <p>{item.review_notes || "Needs manual review"}</p>
                          ) : (
                            <p>Confidence: {((item.confidence_score || 0) * 100).toFixed(0)}%</p>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setEditingItem(item)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      {!item.verified_at && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleVerifyItem(item.id)}
                          disabled={verifyMutation.isPending}
                        >
                          <CheckCircle className="h-4 w-4 text-primary" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Edit dialog */}
      {editingItem && (
        <POLineItemEditor
          item={editingItem}
          onClose={() => setEditingItem(null)}
          userEmail={userEmail}
        />
      )}
    </div>
  );
}
